export interface EngineScore {
  type: "cp" | "mate";
  value: number;
}

export interface EngineLine {
  depth: number;
  multiPv: number;
  score: EngineScore | null;
  pv: string[];
  nodes: number;
  timeMs: number;
}

export interface EngineResult {
  bestMove: string | null;
  line: EngineLine | null;
  lines: EngineLine[];
}

export interface StockfishOptions {
  scriptUrl?: string;
  threads?: number;
  hashMb?: number;
  multiPv?: number;
  initTimeoutMs?: number;
}

export interface AnalyseOptions {
  depth?: number;
  movetime?: number;
}

interface PendingAnalysis {
  resolve: (result: EngineResult) => void;
  reject: (error: Error) => void;
  lines: Map<number, EngineLine>;
  timer: ReturnType<typeof setTimeout> | null;
}

const DEFAULT_SCRIPT_URL = "/engine/stockfish-19-lite-single.js";

function parseInfo(line: string): EngineLine | null {
  if (!line.startsWith("info ")) return null;
  const depthMatch = /\bdepth (\d+)/.exec(line);
  if (!depthMatch) return null;

  const depth = Number(depthMatch[1]);
  const nodesMatch = /\bnodes (\d+)/.exec(line);
  const timeMatch = /\btime (\d+)/.exec(line);
  const pvMatch = /\bpv (.+)$/.exec(line);
  const multiPvMatch = /\bmultipv (\d+)/.exec(line);

  let score: EngineScore | null = null;
  const cpMatch = /\bscore cp (-?\d+)/.exec(line);
  const mateMatch = /\bscore mate (-?\d+)/.exec(line);
  if (cpMatch) score = { type: "cp", value: Number(cpMatch[1]) };
  else if (mateMatch) score = { type: "mate", value: Number(mateMatch[1]) };

  const pv = pvMatch?.[1]?.trim().split(/\s+/) ?? [];

  return {
    depth,
    multiPv: multiPvMatch ? Number(multiPvMatch[1]) : 1,
    score,
    pv,
    nodes: nodesMatch ? Number(nodesMatch[1]) : 0,
    timeMs: timeMatch ? Number(timeMatch[1]) : 0,
  };
}

export class StockfishEngine {
  private worker: Worker | null = null;
  private initPromise: Promise<void> | null = null;
  private pending: PendingAnalysis | null = null;
  private ready = false;
  private options: Required<StockfishOptions>;

  constructor(options: StockfishOptions = {}) {
    this.options = {
      scriptUrl: options.scriptUrl ?? DEFAULT_SCRIPT_URL,
      threads: options.threads ?? 1,
      hashMb: options.hashMb ?? 32,
      multiPv: options.multiPv ?? 3,
      initTimeoutMs: options.initTimeoutMs ?? 30000,
    };
  }

  get isReady(): boolean {
    return this.ready;
  }

  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = new Promise<void>((resolve, reject) => {
      let worker: Worker;
      try {
        worker = new Worker(this.options.scriptUrl);
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
        return;
      }
      this.worker = worker;

      const timeout = setTimeout(() => {
        worker.terminate();
        this.worker = null;
        reject(new Error("Engine failed to start within the timeout"));
      }, this.options.initTimeoutMs);

      worker.onmessage = (event: MessageEvent) => {
        const data = typeof event.data === "string" ? event.data : String(event.data?.data ?? "");
        this.handleMessage(data);
        if (data.includes("uciok")) {
          clearTimeout(timeout);
          this.send("setoption name Threads value " + this.options.threads);
          this.send("setoption name Hash value " + this.options.hashMb);
          if (this.options.multiPv > 1) {
            this.send("setoption name MultiPV value " + this.options.multiPv);
          }
          this.send("isready");
        }
        if (data.includes("readyok")) {
          this.ready = true;
        }
      };

      worker.onerror = (event: ErrorEvent) => {
        clearTimeout(timeout);
        reject(new Error(`Engine worker error: ${event.message}`));
      };

      this.send("uci");
      const waitForReady = setInterval(() => {
        if (this.ready) {
          clearInterval(waitForReady);
          resolve();
        }
      }, 30);
    });
    return this.initPromise;
  }

  async analyse(fen: string, options: AnalyseOptions = {}): Promise<EngineResult> {
    await this.init();
    if (!this.worker) throw new Error("Engine is not running");

    this.stop();

    return new Promise<EngineResult>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending = null;
        reject(new Error("Engine analysis timed out"));
      }, 180000);

      this.pending = { resolve, reject, lines: new Map(), timer };

      this.send("ucinewgame");
      this.send("position fen " + fen);
      if (options.movetime) {
        this.send("go movetime " + options.movetime);
      } else {
        this.send("go depth " + (options.depth ?? 14));
      }
    });
  }

  stop(): void {
    if (this.pending) {
      if (this.pending.timer) clearTimeout(this.pending.timer);
      this.pending.resolve(this.resultFrom(null));
      this.pending = null;
    }
    if (this.worker) this.send("stop");
  }

  setOption(name: string, value: string | number): void {
    this.send(`setoption name ${name} value ${value}`);
  }

  dispose(): void {
    this.stop();
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.ready = false;
    this.initPromise = null;
  }

  private resultFrom(bestMove: string | null): EngineResult {
    const lines = [...(this.pending?.lines.values() ?? [])].sort((a, b) => a.multiPv - b.multiPv);
    return { bestMove, line: lines[0] ?? null, lines };
  }

  private send(command: string): void {
    this.worker?.postMessage(command);
  }

  private handleMessage(data: string): void {
    const trimmed = data.trim();
    if (!trimmed) return;

    if (trimmed.startsWith("bestmove")) {
      const bestMove = trimmed.split(/\s+/)[1] ?? null;
      if (this.pending) {
        if (this.pending.timer) clearTimeout(this.pending.timer);
        const result = this.resultFrom(bestMove);
        this.pending.resolve(result);
        this.pending = null;
      }
      return;
    }

    const info = parseInfo(trimmed);
    if (info && this.pending) {
      const existing = this.pending.lines.get(info.multiPv);
      if (!existing || info.depth >= existing.depth) {
        this.pending.lines.set(info.multiPv, info);
      }
    }
  }
}
